<template>
    <v-container grid-list-md fluid>
        <v-tabs v-model="activeTab" style="border-radius: 16px; overflow: hidden">
            <v-tab value="Layouts" href="#Layouts">Dossiers de révision</v-tab>
            <v-tab value="AuditChecks" href="#AuditChecks">Contrôles</v-tab>
        </v-tabs>
        <v-tabs-window v-model="activeTab">
            <v-window-item value="Layouts">
                <v-row no-gutters v-if="!$vuetify.display.mobile" class="my-3">
                    <v-col cols="2">
                        <v-btn-primary
                            block
                            @click="$router.push({ name: 'AccountingReviewLayout-new' })"
                        >
                            Ajouter
                        </v-btn-primary>
                    </v-col>
                    <v-col class="pl-2">
                        <v-text-field
                            v-model="search"
                            append-inner-icon="$search"
                            label="Recherche"
                            outlined
                            hide-details
                            clearable
                            @click:clear="search = ''"
                            density="compact"
                        />
                    </v-col>
                    <v-col cols="2" class="pl-2">
                        <v-combobox
                            v-model="filterCompany"
                            :items="allAvailableCompanies"
                            label="Société"
                            item-title="key"
                            dense
                            outlined
                            hide-details
                        >
                        </v-combobox>
                    </v-col>
                </v-row>
                <v-row
                    no-gutters
                    class="d-flex justify-center"
                    :style="'max-height:' + maxHeight + ';' + 'overflow:auto'"
                >
                    <v-col cols="8">
                        <v-card
                            class="default_card_wm"
                            style="background: #fff !important"
                            elevation="0"
                        >
                            <v-row class="d-flex py-1">
                                <v-col cols="12">
                                    <v-data-table
                                        title
                                        :headers="headers"
                                        :items="sortedAccountingReviewLayouts"
                                        :items-per-page="-1"
                                        hide-default-footer
                                        class="table"
                                    >
                                        <template v-slot:item="props">
                                            <tr :key="props.item.id" v-if="props.item">
                                                <td width="5%" />
                                                <td
                                                    v-if="props.item.name"
                                                    width="60%"
                                                    class="text-left"
                                                >
                                                    {{ props.item.name }}
                                                </td>
                                                <td width="10%">
                                                    <div class="d-flex justify-center">
                                                        <v-btn-icon
                                                            :icon="
                                                                isAccountingFirmOwner(props.item)
                                                                    ? '$oss-edit'
                                                                    : '$oss-eye'
                                                            "
                                                            color="secondary"
                                                            elevation="0"
                                                            @click="
                                                                $router.push({
                                                                    name: 'AccountingReviewLayout',
                                                                    params: {
                                                                        id: props.item.id,
                                                                    },
                                                                })
                                                            "
                                                            :disabled="readOnly"
                                                            size="x-small"
                                                        />
                                                    </div>
                                                </td>
                                                <td width="10%">
                                                    <div class="d-flex justify-center">
                                                        <v-btn-icon
                                                            icon="$oss-duplicate"
                                                            color="secondary"
                                                            elevation="0"
                                                            @click="duplicate(props.item.id)"
                                                            :disabled="readOnly"
                                                            size="x-small"
                                                        />
                                                    </div>
                                                </td>
                                                <td width="10%">
                                                    <div class="d-flex justify-center">
                                                        <v-btn-trash
                                                            v-if="isAccountingFirmOwner(props.item)"
                                                            @click="
                                                                toggleDeleteAccountingReviewLayoutDialog();
                                                                selectItemToDelete(props.item.id);
                                                            "
                                                            :disabled="readOnly"
                                                            size="x-small"
                                                        />
                                                    </div>
                                                </td>
                                                <td width="5%" />
                                            </tr>
                                        </template>
                                    </v-data-table>
                                </v-col>
                            </v-row>
                        </v-card>
                    </v-col>
                </v-row>
                <oss-dialog
                    v-if="deleteAccountingReviewLayoutDialog"
                    v-model="deleteAccountingReviewLayoutDialog"
                    title="Souhaitez-vous vraiment supprimer ce dossier de révision ?"
                    validTitle="Supprimer"
                    :error="true"
                    @valid="deleteAccountingReviewLayout(itemToDelete)"
                    @close="resetItemToDelete()"
                    min-width="1000px"
                >
                </oss-dialog>
            </v-window-item>
            <v-window-item value="AuditChecks">
                <AuditChecks
                    :auditCheckTypes="auditCheckTypes"
                    mode="enableActions"
                    ref="auditChecks"
                    @displayExistingAudit="displayExistingAudit($event)"
                >
                </AuditChecks>
                <oss-dialog
                    v-if="displayAuditCheckEdition"
                    v-model="displayAuditCheckEdition"
                    title="Affichage d'un contrôle"
                    :validTitle="
                        auditToDisplay && auditToDisplay.type === 'ACCOUNTING_REVIEW_MANUAL_CUSTOM'
                            ? 'Enregistrer'
                            : 'Fermer'
                    "
                    @valid="
                        auditToDisplay && auditToDisplay.type === 'ACCOUNTING_REVIEW_MANUAL_CUSTOM'
                            ? validateModificatedAuditRow()
                            : toggleDisplayAuditCheckEdition()
                    "
                    min-width="1000px"
                >
                    <AuditCheckEdition
                        ref="auditCheckEdition"
                        v-if="displayAuditCheckEdition"
                        :auditToDisplay="auditToDisplay"
                        :readOnly="true"
                    >
                    </AuditCheckEdition>
                </oss-dialog>
            </v-window-item>
        </v-tabs-window>
    </v-container>
</template>

<script lang="ts">
import { Apollo } from "@/decorators";
import { User } from "../../store/store";
import AuditChecks from "../admin/AuditChecks.vue";

import ACCOUNTING_REVIEW_LAYOUTS from "../../graphql/audit/AccountingReviewLayouts.gql";
import ACCOUNTING_REVIEW_LAYOUTS_SCOPING from "../../graphql/audit/AccountingReviewLayoutsScoping.gql";
import DELETE_ACCOUNTING_REVIEW_LAYOUT from "../../graphql/audit/DeleteAccountingReviewLayout.gql";

import { Component, Prop, Vue, toNative } from "vue-facing-decorator";
import AuditCheckEdition from "../admin/AuditCheckEdition.vue";

@Component({
    components: {
        AuditChecks,
        AuditCheckEdition,
    },
})
export class AccountingReviewLayoutsTable extends Vue {
    @Prop({ default: "" }) id: String;

    displayAuditCheckEdition: boolean = false;

    deleteAccountingReviewLayoutDialog: boolean = false;

    readOnly: boolean = false;

    auditToDisplay: any = null;

    auditCheckTypes = [
        "ACCOUNTING_REVIEW_AUTO",
        "ACCOUNTING_REVIEW_MANUAL",
        "ACCOUNTING_REVIEW_MANUAL_CUSTOM",
    ];

    auditCheckTypeSelected = "";

    activeTab: string = "Layouts";

    search = "";

    itemToDelete: string = "";

    headers = [
        {
            title: " ",
            sortable: false,
        },
        {
            title: "Nom",
            sortable: false,
            align: "left",
            class: "subtitle-2",
        },
        {
            title: "Modifier",
            sortable: false,
            align: "center",
            class: "subtitle-2",
        },
        {
            title: "Dupliquer",
            sortable: false,
            align: "center",
            class: "subtitle-2",
        },
        {
            title: "Supprimer",
            sortable: false,
            align: "center",
            class: "subtitle-2",
        },
        {
            title: " ",
            sortable: false,
        },
    ];

    filterCompany = {
        key: "",
        value: "",
    };

    get filterCompanyId() {
        return this.filterCompany?.key && this.filterCompany?.value
            ? this.filterCompany.value
            : null;
    }

    get allAvailableCompanies() {
        const companies = [];
        this.currentUser.allAvailableCompanies.forEach((company) => {
            companies.push({
                value: company.id,
                key: company.name,
            });
            companies.sort((a, b) => {
                if (a.key < b.key) return -1;
                if (a.key > b.key) return 1;
                return 0;
            });
        });
        companies.unshift({ value: "", key: "Toutes mes règles" });

        return companies;
    }

    get currentUser(): User {
        return this.$store.state.user;
    }

    get accountingFirmId() {
        return this.$store.state.user.company.id;
    }

    get sortedAccountingReviewLayouts() {
        // if search return search and sorted
        if (this.search) {
            return this.accountingReviewLayouts.filter((arl) => {
                return arl.name.toLowerCase().includes(this.search.toLowerCase());
            });
        } else {
            return this.accountingReviewLayouts.sort((a, b) => {
                if (a.name < b.name) return -1;
                if (a.name > b.name) return 1;
                return 0;
            });
        }
    }

    get maxHeight() {
        const innHeight = window.innerHeight;
        const vhHeight = (230 / innHeight) * 100;
        const maxHeight_vh = 100 - vhHeight;

        return maxHeight_vh + "vh";
    }

    @Apollo({
        query() {
            return this.filterCompanyId
                ? ACCOUNTING_REVIEW_LAYOUTS_SCOPING
                : ACCOUNTING_REVIEW_LAYOUTS;
        },
        variables() {
            return this.filterCompanyId
                ? {
                      companyId: this.filterCompanyId,
                  }
                : {
                      accountingFirmId: this.accountingFirmId,
                  };
        },
        skip() {
            return !this.accountingFirmId;
        },
        result({ data, loading }: any) {
            if (data?.accountingReviewLayouts) {
                this.accountingReviewLayouts = data.accountingReviewLayouts.map((arl) => {
                    if (
                        arl.name === "Dossier de révision bobbee" ||
                        arl.name === "Dossier de révision bobbee BNC"
                    ) {
                        return {
                            ...arl,
                            isBobbeeOfficialLayout: true,
                        };
                    } else {
                        return arl;
                    }
                });

                this.accountingReviewLayouts.sort((a, b) => {
                    if (a.isBobbeeOfficialLayout) {
                        return -1;
                    }
                    return 1;
                });
            } else {
                if (data?.accountingReviewLayoutsScoping) {
                    this.accountingReviewLayouts = data.accountingReviewLayoutsScoping.map(
                        (arl) => {
                            if (
                                arl.name === "Dossier de révision bobbee" ||
                                arl.name === "Dossier de révision bobbee BNC"
                            ) {
                                return {
                                    ...arl,
                                    isBobbeeOfficialLayout: true,
                                };
                            } else {
                                return arl;
                            }
                        },
                    );

                    this.accountingReviewLayouts.sort((a, b) => {
                        if (a.isBobbeeOfficialLayout) {
                            return -1;
                        }
                        return 1;
                    });
                } else {
                    this.accountingReviewLayouts = [];
                }
            }
        },
    })
    accountingReviewLayouts: any = [];

    isAccountingFirmOwner(arl) {
        return arl?.companyScope?.accountingCompanyOwner?.id === this.accountingFirmId;
    }

    toggleDeleteAccountingReviewLayoutDialog() {
        return (this.deleteAccountingReviewLayoutDialog = !this.deleteAccountingReviewLayoutDialog);
    }

    toggleDisplayAuditCheckEdition() {
        return (this.displayAuditCheckEdition = !this.displayAuditCheckEdition);
    }

    duplicate(id) {
        this.$router.push({
            name: "AccountingReviewLayout-new-duplicate",
            replace: true,
            params: { idToDuplicate: id },
        });
    }

    selectItemToDelete(id) {
        this.itemToDelete = id;
    }

    resetItemToDelete() {
        this.itemToDelete = "";
    }

    displayExistingAudit(audit) {
        this.toggleDisplayAuditCheckEdition();
        this.auditToDisplay = audit;
    }

    validateModificatedAuditRow() {
        // @ts-ignore
        this.$refs.auditCheckEdition.saveAuditModifications();
        // @ts-ignore
        this.$refs.auditChecks.refresh();
        this.toggleDisplayAuditCheckEdition();
    }

    async deleteAccountingReviewLayout(id) {
        const result = await this.$apollo.mutate({
            mutation: DELETE_ACCOUNTING_REVIEW_LAYOUT,
            variables: {
                id: id,
            },
        });

        if (result && result.data) {
            this.accountingReviewLayouts = this.accountingReviewLayouts.filter(
                (accountingReviewLayout) =>
                    accountingReviewLayout.id !== result.data.deleteAccountingReviewLayout.id,
            );
            this.toggleDeleteAccountingReviewLayoutDialog();
            this.resetItemToDelete();
        }
    }

    // @Watch("filterCompanyId")
    // filterCompanyIdWatch() {
    //    debugger;
    //    this.$apollo.queries.partitionTags.refetch();
    // }
}

export default toNative(AccountingReviewLayoutsTable);
</script>
